import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import { CartItem as CartItemType } from '../../context/CartContext';
import { theme } from '../../styles/theme';
import { useCart } from '../../context/CartContext';

interface CartItemProps {
  item: CartItemType;
}

const StyledCartItem = styled.div`
  display: flex;
  border-bottom: 1px solid ${theme.colors.gray};
  padding: ${theme.spacing.md} 0;
  
  &:last-child {
    border-bottom: none;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    padding: ${theme.spacing.md} 0;
  }
`;

const ImageContainer = styled.div`
  width: 100px;
  height: 100px;
  border-radius: ${theme.borderRadius.small};
  overflow: hidden;
  margin-right: ${theme.spacing.md};
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    margin-bottom: ${theme.spacing.sm};
    width: 80px;
    height: 80px;
    float: left;
    margin-right: ${theme.spacing.sm};
  }
`;

const ItemDetails = styled.div`
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const ItemHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: ${theme.spacing.sm};
  }
`;

const ItemTitle = styled.h3`
  font-size: 1.125rem;
  margin: 0;
`;

const ItemCategory = styled.p`
  color: ${theme.colors.gray};
  font-size: 0.875rem;
  text-transform: capitalize;
  margin: ${theme.spacing.xs} 0;
`;

const ItemPrice = styled.p`
  font-weight: 600;
  font-size: 1.125rem;
  margin: 0;
  white-space: nowrap;
`;

const ItemActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: ${theme.spacing.sm};
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    flex-wrap: wrap;
    gap: ${theme.spacing.sm};
    
    > * {
      flex: 1 1 auto;
    }
    
    > *:last-child {
      flex: 0 0 100%;
      justify-content: flex-end;
      display: flex;
    }
  }
`;

const QuantityControl = styled.div`
  display: flex;
  align-items: center;
  
  button {
    width: 30px;
    height: 30px;
    border-radius: ${theme.borderRadius.small};
    background-color: ${theme.colors.lightGray};
    border: none;
    cursor: pointer;
    display: flex;
    justify-content: center;
    align-items: center;
    
    &:hover {
      background-color: ${theme.colors.gray};
    }
    
    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }
  
  span {
    margin: 0 ${theme.spacing.sm};
    width: 30px;
    text-align: center;
    font-weight: 500;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    button {
      width: 26px;
      height: 26px;
    }
    
    span {
      width: 24px;
      margin: 0 ${theme.spacing.xs};
      font-size: 0.9rem;
    }
  }
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${theme.colors.error};
  cursor: pointer;
  display: flex;
  align-items: center;
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: 0.875rem;
  
  svg {
    margin-right: ${theme.spacing.xs};
  }
  
  &:hover {
    text-decoration: underline;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: ${theme.spacing.xs} 0;
    justify-content: flex-end;
  }
`;

const TotalPrice = styled.p`
  font-weight: 600;
  margin: 0;
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    text-align: right;
  }
`;

const CartItem: React.FC<CartItemProps> = ({ item }) => {
  const { updateQuantity, removeItem } = useCart();
  
  const handleUpdateQuantity = (change: number) => {
    const newQuantity = item.quantity + change;
    if (newQuantity >= 1) {
      updateQuantity(item.id, newQuantity);
    }
  };
  
  const handleRemove = () => {
    removeItem(item.id);
  };
  
  return (
    <StyledCartItem>
      <ImageContainer>
        <img src={item.image} alt={item.name} />
      </ImageContainer>
      
      <ItemDetails>
        <ItemHeader>
          <div>
            <ItemTitle>{item.name}</ItemTitle>
            <ItemCategory>{item.category ? item.category.replace('-', ' ') : ''}</ItemCategory>
          </div>
          <ItemPrice>${item.price.toFixed(2)}</ItemPrice>
        </ItemHeader>
        
        <ItemActions>
          <QuantityControl>
            <button 
              onClick={() => handleUpdateQuantity(-1)}
              disabled={item.quantity <= 1}
            >
              -
            </button>
            <span>{item.quantity}</span>
            <button onClick={() => handleUpdateQuantity(1)}>+</button>
          </QuantityControl>
          
          <TotalPrice>${(item.price * item.quantity).toFixed(2)}</TotalPrice>
          
          <RemoveButton onClick={handleRemove}>
            <FontAwesomeIcon icon={faTrashAlt} /> Remove
          </RemoveButton>
        </ItemActions>
      </ItemDetails>
    </StyledCartItem>
  );
};

export default CartItem; 